
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src1738611268/src/vl-app/pages/patients/register.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { useCallback, useEffect, useState } from 'react';
import { useRegistrationFormInstance } from '@components/Patients/Registration/useSelfRegistrationFormInstance';
import * as actions from '@actions';
import { Duration } from 'luxon';
import { FaCheck } from 'react-icons/fa';
import { Radio, Space, RadioChangeEvent } from 'antd';
import { Form } from 'ALIASED-antd';
import styled from '@emotion/styled';
import { parseAsInteger, useQueryState } from '@lib/queryState';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Auth, type CognitoUser } from 'aws-amplify';
import useAuth from '@vl-core/useAuth';
import { useRouter } from '@hooks/useRouter';
import Header from '@components/Layouts/Header/Header';
import { getLogger } from 'loglevel';
import { useRuntimeConfig } from '@vl-core/hooks/useConfig';
import { toast, type Toast } from 'react-hot-toast';
import { setToastError } from 'vl-common/src/lib/toastUtils';
import { infoModal } from 'vl-common/src/components/Modals';
import { AnimatePresence, motion } from 'motion/react';
import { useTimingEvents } from '@hooks/useTimingEvents';
import AsyncButton from '@components/UI/AsyncButton/AsyncButton';
import Progress from '@components/Register/Progress';
import NavigationButtons from '@components/Register/NavigationButtons';
import MFAInput from '@components/FormInputs/MFAInput';
import Grid from '@components/UI/Grid';
import MobileInput from '@components/Patients/MobileInput/MobileInput';
import { useRegistrationUser } from '@hooks/useRegistrationUser';
import { css } from '@emotion/react';
import { Maintenance } from 'vl-common/src/components/Maintenance';

import { RegistrationForm } from '@components/Patients/Registration/RegistrationForm';
import { RegistrationFormItem } from '@components/Patients/Registration/RegistrationFormItem';
import { FirstNameFormItem } from '@components/Patients/Registration/items/FirstNameFormItem';
import { TRegistrationForm } from '@components/Patients/Registration/TRegistrationForm';
import { PasswordFormItem } from '@components/Patients/Registration/items/PasswordFormItem';

import { EmailFormItem } from '@components/Patients/Registration/items/EmailFormItem';
import { LastNameFormItem } from '@components/Patients/Registration/items/LastNameFormItem';
import { CaptchaFormItem } from '@components/Patients/Registration/items/CaptchaFormItem';
import { TermsCheckboxFormItem } from '@components/Patients/Registration/items/TermsCheckboxFormItem';
import type { ErrorWithResponse, VLThunkAction } from 'store/actions/action';
import { CreateAccountSteps, StyledH1 } from '../create-account';
import i18n from 'vl-common/src/lib/i18n';

const t = i18n.getFixedT(null, 'main');

const log = getLogger(__filename);

type TFormState = {
  accepted?: boolean;
  email_validated?: boolean;
  mobile_validated?: boolean;
  notification_preference?: 'SMS' | 'EMAIL';
  mfa_mobile?: string;
  mfaCode?: string;
  pendingTimings?: VLThunkAction<any>[];
};

class MyErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
  },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: unknown) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: ErrorWithResponse, errorInfo: React.ErrorInfo) {
    const { hasError } = this.state;

    console.log('Toast', { error, errorInfo, hasError });
    delete error.stack;

    if (typeof error.response?.json === 'function') {
      error.response.json().then(
        (response) => {
          toast.error(
            (toast: Toast) => {
              setToastError(toast, error);
              return (
                <p style={{ margin: '30px 15px', fontSize: 'x-large' }}>
                  {response.message || 'Account creation failed'}
                </p>
              );
            },
            {
              duration: Infinity
            }
          );
        },
        () => {
          toast.error(<p>{t('Account creation failed')}</p>, { duration: Infinity });
        }
      );
      return;
    }

    console.log('Apparently is not a ErrorWithResponse');
    toast.error(<p>{t('Account creation failed')}</p>, { duration: Infinity });
  }

  render() {
    const { children } = this.props;
    return <div className="error-boundary">{children}</div>;
  }
}

const warnUserTemporaryPassword = async () => {
  const modal = infoModal({ title: 'Password Incorrect' });

  return new Promise<void>((resolve) =>
    modal.update({
      content: (
        <div>
          <p>{t('This account already exists, please log in via the login page or reset your pass')}</p>
          <p>{t('Redirecting to login page.')}</p>
        </div>
      ),
      onOk: () => {
        resolve();
        return false;
      }
    })
  );
};

const warnUserAccountAlreadyExists = async () => {
  const modal = infoModal({ title: 'Account Exists' });

  return new Promise<void>((resolve) =>
    modal.update({
      content: (
        <div>
          <p>{t('This account already exists. Please login on the login page with your existing p')}</p>
          <p>
            {t('If you have forgotten your password click on the ')}
            <i>{t('Click here to reset it')}</i> {t(' link.')}
          </p>
        </div>
      ),
      onOk: () => {
        resolve();
        return false;
      }
    })
  );
};

const warnCouldNotAccessBackend = async () => {
  const modal = infoModal({ title: 'Cannot Access' });

  return new Promise<void>((resolve) =>
    modal.update({
      content: (
        <div>
          <p>{t('Could not access the HBSUK servers.')}</p>
          <br />
          {t('Please try again later.')}
          <br />
          <p>{t('If the problem persists, try connecting using a different network or contact sup')}</p>
        </div>
      ),
      onOk: () => {
        resolve();
        return false;
      }
    })
  );
};

const Ticked = ({ ticked }: { ticked: boolean }) => (
  <span
    data-ticked={ticked}
    css={css`
      display: none !important;
      vertical-align: baseline;
      margin-left: 4px;

      &[data-ticked='true'] {
        display: inline !important;
      }
    `}
  >
    <FaCheck color="green" size="1rem" aria-hidden />
  </span>
);

const WhoAreYou = ({
  nextStep,
  onCogUser = () => {}
}: {
  nextStep: (updates: TFormState) => void;
  onCogUser: (cognito: CognitoUser) => void;
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const router = useRouter();
  const { query } = router;
  const [submitting, setSubmitting] = useState(false);
  const { SELF_REFER_CLIENT_ID } = useRuntimeConfig();

  const form = useRegistrationFormInstance();

  return (
    <RegistrationForm
      form={form}
      onFinish={async (data) => {
        try {
          let wasSubmitting = false;
          setSubmitting((s) => {
            wasSubmitting = s;
            return true;
          });

          if (wasSubmitting) return;

          const { first_name: first, last_name: last, email, password, captcha, checkbox: accepted } = data;

          const clientId = Number(SELF_REFER_CLIENT_ID);
          const response = await dispatch(
            actions.createSelf(first, last, email, password, captcha || undefined, 'self_refer' in query, clientId),
            false
          );

          if (response.payload?.status === 409) {
            await warnUserAccountAlreadyExists();
            await router.replace(`/login/?username=${email}`);
            return;
          }

          if (response.error?.status === 400) {
            infoModal({
              title: 'Bad captcha code',
              content: `Please try again - you will need to redo the "I'm not a robot" check`
            });
            return;
          }

          if (response.error?.status === 599) {
            await warnCouldNotAccessBackend();
            await router.replace(`/login/?username=${email}`);
            return;
          }

          if (response.error?.status === 406) {
            const body = await response.error.json();
            const message = body?.errorMessage || 'Password is easily predictable, please update';

            infoModal({
              title: 'Bad password',
              content: /^\d{3} - /.test(message) ? message.slice(6) : message
            });
            return;
          }

          if (response.payload?.errorMessage) {
            infoModal({ title: 'Could not create account', content: response.payload?.errorMessage });
            return;
          }

          try {
            const cog = await Auth.signIn(email, password, {
              authFlowType: 'CUSTOM_WITH_SRP'
            });

            if (cog.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
              const userData = btoa(JSON.stringify(cog));
              router.replace(`/login/?username=${email}&pageView=newPassword&userdata=${userData}`).then();
              return;
            }

            onCogUser(cog);
          } catch (e) {
            if (e && typeof e === 'object' && 'code' in e && e.code === 'NotAuthorizedException') {
              await warnUserTemporaryPassword();
              router.replace(`/login/?username=${email}`).then();
              return;
            }
          }

          const pendingTimings = [
            actions.usersTimingEvent('REGISTRATION_START'),
            actions.usersTimingEvent('NAME_AND_EMAIL_START'),
            actions.usersTimingEvent('NAME_AND_EMAIL_END')
          ];
          nextStep({ accepted, pendingTimings, email_validated: false });
        } finally {
          setSubmitting(false);
        }
      }}
      layout="vertical"
    >
      <StyledH1>{t('Create an account')}</StyledH1>
      <Grid columns={2}>
        <FirstNameFormItem />

        <LastNameFormItem />

        <EmailFormItem />

        <PasswordFormItem />

        <TermsCheckboxFormItem />

        <CaptchaFormItem />
      </Grid>

      <NavigationButtons
        prevProps={{ onClick: () => router.push('/login'), ['data-testid']: 'previous' }}
        nextProps={{ htmlType: 'submit', loading: submitting, ['data-testid']: 'next' }}
        data-testid="navigation-buttons"
      />
    </RegistrationForm>
  );
};

const ConfirmEmail = ({
  nextStep,
  formState,
  cognito = undefined
}: {
  nextStep: (updates: TFormState) => void;
  formState: TFormState;
  cognito?: CognitoUser;
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const router = useRouter();
  const refetch = 0;
  const [validated, setValidated] = useState<boolean>(formState.email_validated || false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const { MFA_LENGTH, MFA_SECS = 60, MFA_VALIDITY_SECS = 300 } = useRuntimeConfig();

  const user = useRegistrationUser();
  const form = useRegistrationFormInstance();
  const email = Form.useWatch('email', { form, preserve: true });
  const password = Form.useWatch('password', { form, preserve: true });

  const { pendingTimings } = formState;

  useEffect(() => {
    pendingTimings?.push(actions.usersTimingEvent('EMAIL_VERIFICATION_START'));
    return () => {
      if (!pendingTimings) return;
      pendingTimings?.push(actions.usersTimingEvent('EMAIL_VERIFICATION_END'));

      Promise.all(pendingTimings?.map((thunk) => dispatch(thunk))).then(console.log, console.error);
    };
  }, [dispatch]);

  const MFA_SECONDS_TO_MINUTES = Duration.fromObject({ seconds: Number(MFA_VALIDITY_SECS) }).toFormat('m');

  useEffect(() => {
    const signIn = async () => {
      try {
        const cog =
          cognito ||
          (await Auth.signIn(email, password, {
            authFlowType: 'CUSTOM_WITH_SRP'
          }));

        setCognitoUser(cog);

        if (cog.challengeName === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          const userData = btoa(JSON.stringify(cog));
          router.replace(`/login/?username=${email}&pageView=newPassword&userdata=${userData}`).then();
          return;
        }

        if (cog.challengeName !== 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
          log.error('Unexpected response');
        }
      } catch (e) {
        if (e && typeof e === 'object' && 'code' in e && e.code === 'NotAuthorizedException') {
          await warnUserTemporaryPassword();
          router.replace(`/login/?username=${email}`).then();
        }
      }
    };

    if (email && password) {
      signIn().catch(() => {});
    }
  }, [email, password, refetch, user]);

  const handleSubmit = () => {
    if (validated) {
      nextStep({
        email_validated: true,
        mfaCode: form.getFieldValue('mfa-email')
      });
    }
  };

  const resendCode = async (msg = 'Your identity confirmation code has been resent') => {
    console.log({ cognitoUser });

    try {
      await Auth.signOut().catch();

      cognito = await Auth.signIn(email, password);

      setCognitoUser(cognito);
      infoModal({
        title: 'Identity Confirmation Code',
        content: msg
      });
    } catch (e) {
      console.error(e);
      infoModal({
        title: 'Unexpected Response',
        content: 'An unexpected response was received. Please try again later.'
      });
    }
  };

  const handleCodeChanged = async (code: string) => {
    form.setFieldValue('mfa-email', code);
    try {
      if (!cognitoUser) {
        throw Error('No cognito user');
      }
      const r = await Auth.sendCustomChallengeAnswer(cognitoUser, code);

      setValidated(r?.challengeName === 'DONE');
    } catch (e) {
      const message =
        e && typeof e === 'object' && 'message' in e && e.message === 'Invalid session for the user.'
          ? 'Your confirmation is invalid, perhaps it has expired? A fresh code has been resent.'
          : 'Your confirmation is invalid - it has been resent';
      console.log('Auth.sendCustomChallengeAnswer fails', e);

      // Bad session or incorrect code ... request new code ...
      await resendCode(message);
    }
  };

  return (
    <RegistrationForm form={form} onFinish={handleSubmit} layout="horizontal" labelCol={{ span: 8 }}>
      <h2>{t('Confirm Identity')}</h2>

      <p>
        {t('We’ve sent you an email with a ')}
        {MFA_LENGTH}
        {t('-digit security code. This security code is valid for ')}
        {MFA_SECONDS_TO_MINUTES}
        {t(' minutes.')}
      </p>

      <RegistrationFormItem
        name="mfa-email"
        label={t('Code')}
        labelAlign="left"
        rules={[
          {
            validator() {
              if (validated) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('Please validate using the code we have sent to your email address'));
            }
          }
        ]}
      >
        <MFAInput
          clearUserInput={!!refetch}
          validated={validated}
          codeLength={MFA_LENGTH}
          onCodeChanged={handleCodeChanged}
          resetErrorDisplay={() => form.setFields([{ name: 'mfa-email', errors: [] }])}
          resendButton={
            <AsyncButton
              onClick={() => resendCode()}
              disabled={!cognitoUser}
              delay={Number(MFA_SECS)}
              disableInitially
              data-testid="resend-verification-code"
            >
              <AsyncButton.Button data-testid="resend-verification-code-button">
                {t('Resend verification code')}
              </AsyncButton.Button>
              <AsyncButton.TimeLeft data-testid="resend-verification-code-time-left" type="Email" />
            </AsyncButton>
          }
        />
      </RegistrationFormItem>

      <NavigationButtons
        prevProps={{ disabled: true, ['data-testid']: 'previous' }}
        nextProps={{ htmlType: 'submit', ['data-testid']: 'next-submit' }}
        data-testid="navigation-buttons"
      />
    </RegistrationForm>
  );
};

export const SetupMobile = ({
  nextStep = (updates: {}) => {},
  formState,
  hideNavigationButtons = false
}: {
  nextStep?: (updates: TFormState) => void;
  formState?: TFormState;
  hideNavigationButtons?: boolean;
}) => {
  const { t } = useTranslation('common');

  const dispatch = useDispatch();
  const [refetchUser, setRefetchUser] = useState(0);
  const [clearMFA, setClearMFA] = useState(0);
  const [mobile, setMobile] = useState('');
  const [validMobile, setValidMobile] = useState(formState?.mobile_validated);
  const [validated, setValidated] = useState(formState?.mobile_validated);
  const [codeIssued, setCodeIssued] = useState(formState?.mobile_validated);
  const [code, setCode] = useState(formState?.mfa_mobile);
  const [notificationPreference, setNotificationPreference] = useState(formState?.notification_preference);
  const [submitted, setSubmitted] = useState(false);
  const [mobileMFAButtonsDisabled, setMobileMFAButtonsDisabled] = useState(false);
  const [validatingMobile, setValidatingMobile] = useState(false);
  const user = useRegistrationUser(refetchUser);
  const { MFA_LENGTH, MFA_SECS = 60, MFA_VALIDITY_SECS = 300 } = useRuntimeConfig();
  const form = useRegistrationFormInstance();
  // prettier-ignore
  const MFA_SECONDS_TO_MINUTES = Duration.fromObject({ seconds: Number(MFA_VALIDITY_SECS) }).toFormat('m');

  useTimingEvents('MOBILE_VERIFICATION_START', 'MOBILE_VERIFICATION_END');
  useEffect(() => {
    if (user) {
      setMobile(user?.tel_mobile_no || '');
      setNotificationPreference(form.getFieldValue('notification_preference'));
    }
  }, [user]);

  const sendMFAViaSMS = async () => {
    setMobileMFAButtonsDisabled(true);
    await dispatch(
      actions.resendCode({
        user_guid: user?.user_guid,
        method: 'SMS',
        resend: true,
        tel_mobile: mobile
      })
    );
  };

  const submitValues = async (
    value?: 'SMS' | 'EMAIL',
    tel_mobile?: string,
    progressToNextStep = true,
    sendCode = false
  ) => {
    const payload = {
      user_guid: user?.user_guid,
      method: value ?? notificationPreference,
      preference: true,
      tel_mobile
    };

    setMobileMFAButtonsDisabled(true);

    await dispatch(actions.updateNotificationPref(payload));

    setSubmitted(progressToNextStep);

    if (progressToNextStep) {
      nextStep({
        notification_preference: value ?? notificationPreference,
        mobile_validated: validated,
        mfa_mobile: code
      });
    }

    if (value === 'SMS' && sendCode) {
      setCodeIssued(true);
      setRefetchUser((f) => f + 1);
    }

    setSubmitted(false);
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (mobileMFAButtonsDisabled) {
      timeout = setTimeout(
        () => {
          setMobileMFAButtonsDisabled(false);
        },
        Number(MFA_SECS) * 1000
      );
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [mobileMFAButtonsDisabled]);

  useEffect(() => {
    setClearMFA((c) => c + 1);
  }, [mobile]);

  async function testMobile() {
    setValidatingMobile(true);

    await submitValues('SMS', mobile, false, true);
  }

  const handleCodeChanged = async (code: string) => {
    const user_guid = user?.user_guid || '';
    const sendToastOnFailure = false;
    setCode(code);
    const response = await dispatch(
      actions.updateNotificationPref({
        user_guid,
        method: 'SMS',
        code
      }),
      sendToastOnFailure
    );

    const isSuccess = response.pref_notification === 'SMS';

    setValidated(isSuccess);
    setRefetchUser((f) => f + 1);

    if (isSuccess && hideNavigationButtons) {
      submitValues('SMS', mobile, true, false);

      form.setFieldsValue({
        notification_preference: 'SMS',
        tel_mobile: mobile
      });

      setRefetchUser((f) => f + 1);
    }
  };

  const digitsMatch = (left?: string | null, right?: string | null) => {
    const digitsOnly = (s: string) => s?.replace(/\D/g, '');
    return left && right && digitsOnly(left) === digitsOnly(right);
  };

  const onChangePref = ({ target: { value } }: RadioChangeEvent) => {
    setNotificationPreference(value);

    if (value === 'EMAIL' && hideNavigationButtons) {
      submitValues(value, mobile, true, false);
    }
  };

  const fields = (
    <>
      <RegistrationFormItem
        label={t('Contact Preferences')}
        name="notification_preference"
        rules={[{ required: true, message: t('Please select a contact preference') }]}
      >
        <Radio.Group onChange={onChangePref} value={notificationPreference} defaultValue={notificationPreference}>
          <Space direction="vertical">
            <Radio value="EMAIL" data-testid="use-email">
              {t('Use email for all communications')}
            </Radio>
            <Radio value="SMS" data-testid="use-sms">
              {t('Use text messages where possible')}
            </Radio>
          </Space>
        </Radio.Group>
      </RegistrationFormItem>

      {notificationPreference === 'SMS' && (
        <motion.div
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.1 }}
          exit={{ opacity: 0 }}
        >
          <RegistrationFormItem
            label={t('Mobile phone number')}
            htmlFor="phone-number-input"
            hasFeedback
            validateTrigger="onBlur"
            rules={[
              {
                validator() {
                  if (mobile === '') {
                    return Promise.reject(new Error(t('ThisFieldIsRequired')));
                  }
                  if (!validMobile) {
                    return Promise.reject(new Error(t('This field is invalid')));
                  }
                  if (validatingMobile) {
                    if (digitsMatch(user?.tel_mobile_no, mobile)) {
                      return Promise.resolve();
                    }
                    setValidated(false);
                    return Promise.reject(new Error(t('New phone number entered. Please reverify.')));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <div
              css={css`
                display: flex;
                justify-content: left;
                gap: 0px 5px;
                align-items: center;
              `}
            >
              <MobileInput
                inputId="phone-number-input"
                mobile={mobile}
                setMobile={setMobile}
                setValidMobile={setValidMobile}
                location="self-registration"
                separateTelCode={false}
              />

              <AsyncButton
                onClick={testMobile}
                disabled={
                  !validMobile ||
                  (user?.tel_validated && digitsMatch(user?.tel_mobile_no, mobile)) ||
                  mobileMFAButtonsDisabled
                }
                delay={Number(MFA_SECS)}
                data-testid="validate-mobile-button"
              >
                <AsyncButton.Button data-testid="validate-mobile-button">
                  {user?.tel_validated && digitsMatch(user?.tel_mobile_no, mobile)
                    ? t('Verified phone number')
                    : t('Verify phone number')}
                  <Ticked ticked={!!(user?.tel_validated && digitsMatch(user?.tel_mobile_no, mobile))} />
                </AsyncButton.Button>
                <AsyncButton.TimeLeft type="SMS" data-testid="validate-mobile-time-left" />
              </AsyncButton>
            </div>
          </RegistrationFormItem>

          <RegistrationFormItem
            label={t('Text message verification code (valid for {{MFA_SECONDS_TO_MINUTES}} minutes)', {
              MFA_SECONDS_TO_MINUTES
            })}
            validateTrigger="onBlur"
            name="mfa-input"
            rules={[
              {
                validator() {
                  if (user?.tel_validated && digitsMatch(user?.tel_mobile_no, mobile)) {
                    return Promise.resolve();
                  }
                  if (!codeIssued) {
                    return Promise.reject(new Error('Mobile phone number not verified'));
                  }
                  return Promise.reject(new Error('Incorrect verification code entered'));
                }
              }
            ]}
          >
            <MFAInput
              validated={!!validated}
              codeLength={MFA_LENGTH}
              onCodeChanged={handleCodeChanged}
              clearUserInput={!!clearMFA}
              showState={false}
              resendButton={
                <AsyncButton
                  onClick={sendMFAViaSMS}
                  disabled={!codeIssued || user?.tel_validated || mobileMFAButtonsDisabled}
                  delay={Number(MFA_SECS)}
                  data-testid="resend-verification-code"
                >
                  <AsyncButton.Button data-testid="resend-verification-code-button">
                    {t('Resend verification code')}
                  </AsyncButton.Button>
                  <AsyncButton.TimeLeft type="SMS" data-testid="resend-verification-code-time-left" />
                </AsyncButton>
              }
            />
          </RegistrationFormItem>
        </motion.div>
      )}
    </>
  );

  if (hideNavigationButtons) {
    return fields;
  }

  return (
    <AnimatePresence>
      <RegistrationForm
        form={form}
        onFinish={() => submitValues(notificationPreference, mobile, true, false)}
        layout="vertical"
        data-testid="setup-mobile-component"
      >
        {fields}

        <motion.div layout>
          <NavigationButtons
            prevProps={{ disabled: true, ['data-testid']: 'previous-button' }}
            nextProps={{ htmlType: 'submit', loading: submitted, ['data-testid']: 'next-button' }}
            data-testid="navigation-buttons"
          />
        </motion.div>
      </RegistrationForm>
    </AnimatePresence>
  );
};

function CreateAccountStepsWithUser({
  setStep,
  stepOffset = 3,
  initialStep
}: {
  setStep: (step: number) => void;
  stepOffset?: number;
  initialStep: number;
}) {
  const { user } = useAuth();
  const handleStepChange = useCallback(
    (step: number) => {
      setStep(step + stepOffset);
    },
    [setStep, stepOffset]
  );

  if (!user) return null;

  return (
    <MyErrorBoundary>
      <CreateAccountSteps
        allowAmendmentsToExistingFields
        showSteps={false}
        onStepChange={handleStepChange}
        initialStep={initialStep}
        incompleteRegistration={false}
      />
    </MyErrorBoundary>
  );
}
const StyledHeader = styled(Header)``;

const StyledSection = styled.section`
  max-width: 768px;
  margin: 64px auto;
  padding: 0 1em 64px;

  h1 {
    font-weight: bold;
    font-size: 30px;
    color: var(--productGray);
    margin-bottom: 40px;

    > span {
      color: var(--productPrimary);
    }
  }
`;

const SelfRegister = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [step, setStep] = useQueryState('step', parseAsInteger.withDefault(0));
  const [formState, setFormState] = useState<TFormState>({});
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const { SELF_REFER_CLIENT_ID } = useRuntimeConfig();
  const { t } = useTranslation('common');

  const [form] = Form.useForm<TRegistrationForm>();

  useEffect(() => {
    // When I eventually navigate away from self registration, if I created a new user, record the end of the
    // registration process
    async function recordRegistrationComplete() {
      try {
        const cog = await Auth.currentSession();
        if (!cog) throw Error('No cognito user');
        const userGuid = cog.getIdToken().payload?.sub;

        if (userGuid) {
          await dispatch(actions.usersTimingEvent('REGISTRATION_END'));
        }
      } catch (e) {
        console.error(e);
      }
    }
    return recordRegistrationComplete as () => void;
  }, [dispatch]);

  useTimingEvents('REGISTRATION_START', 'REGISTRATION_END');

  if (!Number(SELF_REFER_CLIENT_ID)) {
    throw Error('Please set up a "SELF_REFER_CLIENT_ID"');
  }

  // todo - normally would wrap with useCallback(), but this doesn't work because the functional
  //        update to setStep() does not work as expected. See VL2-1429
  const nextStep = (updates: TFormState) => {
    setStep(step + 1);
    setFormState((s) => ({ ...s, ...updates }));
  };

  useEffect(() => {
    const createAccount = async () => {
      if (step === 2) {
        await dispatch(actions.userFetch(normaliseRecord(router.query)));
      }
    };

    createAccount().then();
  }, [router, dispatch, step]);

  return (
    <RegistrationForm form={form} component="div">
      <StyledHeader pageType="login" pageTitle={`${t('SelfRegistrationStep')} ${step + 1}`} />
      <Progress currentStep={step} stepNames={['Account', 'Verify', 'Messages', 'Identity', 'Address', 'GP']} />

      <StyledSection>
        {step === 0 && <WhoAreYou key="who-are-you" nextStep={nextStep} onCogUser={(u) => setCognitoUser(u)} />}

        {step === 1 && <ConfirmEmail formState={formState} nextStep={nextStep} cognito={cognitoUser} />}

        {step >= 2 && <CreateAccountStepsWithUser setStep={setStep} stepOffset={2} initialStep={step - 2} />}
      </StyledSection>
    </RegistrationForm>
  );
};

// NB: WrappedSelfRegister effectively allows the parent component to mount properly before starting to create
// SelfRegister. This enables the calling HBSApp instance to reset its query cache before mounting SelfRegister.
function WrappedSelfRegister() {
  const [mounted, setMounted] = useState(false);

  const { SYSTEM_MODE } = useRuntimeConfig();

  useEffect(() => {
    setMounted(() => true);
  }, []);

  if (!mounted) return null;

  if (SYSTEM_MODE === 'bvt') return <Maintenance />;

  return <SelfRegister />;
}

export default WrappedSelfRegister;

const normaliseRecord = (input: Record<string, string | string[] | undefined>): Record<string, string> => {
  return Object.fromEntries(
    Object.entries(input)
      .filter((entry): entry is [string, string | string[]] => entry[1] !== undefined)
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : value])
  );
};
export const Route = createFileRoute('/patients/register')({ component: WrappedSelfRegister });
